import { StoryblokComponent, StoryblokComponentList } from "@trydig/storyblok";
import { clsx } from "@trydig/ui";
import type { ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { Accordion, type AccordionItem } from "@/components/ui/Accordion";
import { Asset } from "@/components/ui/Asset";
import { Container } from "@/components/ui/Container";
import { Prose } from "@/components/ui/Prose";
import { Typography } from "@/components/ui/Typography";
import type { AssetStoryblok, EmployeeStoryblok, TextStoryblok } from "@/generated/sb/sb-types";
import { isDarkMode } from "@/lib/utils/isDarkMode";

export type TextAlignment = "center" | "left" | "right";

export interface TextProps {
	alignment?: TextAlignment;
	bg?: string;
	heading?: string;
	sideLabel?: string;
	body?: ReactNode;
	quoteCaption?: string;
	quoteText?: string;
	accordion?: AccordionItem[];
	asset?: AssetStoryblok;
	contact?: TextStoryblok["contact"];
	secondary?: TextStoryblok["secondary"];
	className?: string;
	preview: boolean;
}

/**
 * Per-alignment layout rules. Each alignment picks the outer shape and
 * content column placement within the 12-col layout grid.
 */
const layoutFor = (alignment: TextAlignment, hasSideLabel: boolean) => {
	if (alignment === "center") {
		return { useGrid: false, contentClass: "mx-auto w-full max-w-[40.5rem]" };
	}
	if (alignment === "left") {
		return { useGrid: true, contentClass: "col-span-12 lg:col-span-8" };
	}
	return {
		useGrid: true,
		contentClass: hasSideLabel
			? "col-span-12 lg:col-start-7 lg:col-span-6"
			: "col-span-12 lg:col-start-6 lg:col-span-7",
	};
};

export const Text = ({
	alignment = "center",
	bg,
	heading,
	sideLabel,
	body,
	quoteCaption,
	quoteText,
	accordion,
	asset,
	contact,
	secondary,
	className,
	preview,
}: TextProps) => {
	const hasSideLabel = alignment === "right" && Boolean(sideLabel);
	const { useGrid, contentClass } = layoutFor(alignment, hasSideLabel);

	// When the right column contains only the contact card, vertically center the
	// side label with it — matches the design system's right-alignment-with-contact
	// variant. Other content (heading/body/quote/accordion/asset/secondary) keeps
	// the side label top-aligned.
	const onlyContact =
		!heading &&
		!body &&
		!quoteCaption &&
		!quoteText &&
		!(accordion && accordion.length > 0) &&
		!asset?.url &&
		!secondary &&
		Boolean(contact);

	const content = (
		<div className="flex w-full flex-col gap-grid-section-vertical-md">
			{heading && (
				<Typography
					variant="heading-display-sm"
					as="h2"
					className={clsx("text-text-dark-text-1", isDarkMode(bg) && "text-text-light-text-1")}
				>
					{heading}
				</Typography>
			)}

			{(body || quoteCaption || quoteText || (accordion && accordion.length > 0)) && (
				<div className="flex flex-col gap-grid-section-vertical-md">
					{body && (
						<Prose className={clsx(isDarkMode(bg) && "text-text-light-text-1")}>{body}</Prose>
					)}

					{(quoteCaption || quoteText) && (
						<div className="flex flex-col gap-grid-section-vertical-sm text-text-dark-text-1">
							{quoteCaption && (
								<Typography
									variant="label-sm-reg"
									as="p"
									className={clsx(isDarkMode(bg) && "text-text-light-text-1")}
								>
									{quoteCaption}
								</Typography>
							)}
							{quoteText && (
								<Typography
									variant="heading-md"
									as="p"
									className={clsx("leading-[1.25]", isDarkMode(bg) && "text-text-light-text-1")}
								>
									{quoteText}
								</Typography>
							)}
						</div>
					)}

					{accordion && accordion.length > 0 && <Accordion items={accordion} allowMultiple />}
				</div>
			)}

			{contact && typeof contact !== "string" && (
				<StoryblokComponent blok={contact.content} preview={preview} darkMode={isDarkMode(bg)} />
			)}

			{asset?.filename && (
				<div className="relative aspect-[16/9] w-full overflow-hidden">
					<Asset asset={asset} sizes="(min-width: 1024px) 60vw, 100vw" />
				</div>
			)}

			{secondary && <StoryblokComponentList bloks={secondary} preview={preview} />}
		</div>
	);

	return (
		<section
			className={twMerge("py-grid-section-vertical-lg", className)}
			style={bg ? { backgroundColor: bg } : undefined}
		>
			<Container>
				{useGrid ? (
					<div
						className={twMerge(
							"grid grid-cols-12 gap-[var(--layout-gap)]",
							onlyContact && "lg:items-center",
						)}
					>
						{hasSideLabel && (
							<Typography
								variant="heading-display-sm"
								as="p"
								className="col-span-12 text-text-dark-text-1 lg:col-span-5"
							>
								{sideLabel}
							</Typography>
						)}
						<div className={contentClass}>{content}</div>
					</div>
				) : (
					<div className={contentClass}>{content}</div>
				)}
			</Container>
		</section>
	);
};
